<script setup>
import { computed } from "vue";
import { Search } from "lucide-vue-next";

import { Input } from "@/shared/ui/input";
import { Spinner } from "@/shared/ui/spinner";
import { Empty } from "@/shared/ui/empty";
import { UserAvatar } from "@/shared/ui/user-avatar";
import { Kbd } from "@/shared/ui/kbd";
import { getRegionLabel } from "@/shared/lib/regionLabels";

const props = defineProps({
  notaries: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  assigning: { type: Boolean, default: false },
  search: { type: String, default: "" },
  focusedIndex: { type: Number, default: -1 },
  totalCount: { type: Number, default: 0 },
  showHeader: { type: Boolean, default: false },
  showFooter: { type: Boolean, default: true },
  bare: { type: Boolean, default: false },
});

const emit = defineEmits(["update:search", "update:focusedIndex", "select"]);

const searchModel = computed({
  get: () => props.search,
  set: (v) => emit("update:search", v),
});

function buildFullName(n) {
  if (!n) return "";
  return [n.last_name, n.first_name, n.middle_name].filter(Boolean).join(" ");
}

function onRowClick(n) {
  emit("select", n);
}

function onRowHover(i) {
  emit("update:focusedIndex", i);
}
</script>

<template>
  <div class="panel" :class="{ 'panel--bare': bare }">
    <div v-if="showHeader" class="panel-head">
      <div class="panel-title-row">
        <div class="panel-title">
          <Users class="w-[14px] h-[14px]" />
          {{ $t("Нотариусы") }}
        </div>
        <div class="panel-count">{{ totalCount }}</div>
      </div>
      <div class="search-wrapper">
        <Search class="search-icon" />
        <Input
          v-model="searchModel"
          :placeholder="$t('Поиск по имени, ИИН или email')"
          class="search-input pl-9 pr-7"
          :disabled="assigning"
        />
      </div>
    </div>

    <!-- Standalone search when no header -->
    <div v-else class="panel-head panel-head--bare">
      <div class="search-wrapper">
        <Search class="search-icon" />
        <Input
          v-model="searchModel"
          :placeholder="$t('Поиск по имени')"
          class="search-input pl-9 pr-7"
          :disabled="assigning"
        />
      </div>
    </div>

    <div class="list-wrapper" :class="{ 'is-locked': assigning }">
      <div v-if="loading || assigning" class="loader">
        <Spinner size="sm" />
      </div>

      <Empty
        v-else-if="!notaries.length"
        :description="$t('Нотариусы не найдены')"
      />

      <ul v-else class="notary-list">
        <li
          v-for="(n, i) in notaries"
          :key="n.uuid"
          class="notary-row"
          :data-row-index="i"
          :data-focused="focusedIndex === i"
          @click="onRowClick(n)"
          @mouseenter="onRowHover(i)"
        >
          <UserAvatar
            :name="buildFullName(n)"
            :identifier="n.identifier"
            :seed="n.uuid"
            :status="n.is_online ? 'active' : null"
            :size="34"
          />
          <div class="row-info">
            <div class="row-name">
              <div class="truncate">
                {{ buildFullName(n) || n.identifier }}
              </div>
            </div>
            <div class="row-meta">
              <div v-if="n.organization_code" class="row-region">
                {{ getRegionLabel(n.organization_code) }}
              </div>
              <div v-if="n.organization_code" class="row-meta-dot" />
              <div class="truncate">
                {{ n.email || n.identifier }}
              </div>
            </div>
          </div>
        </li>
      </ul>
    </div>

    <div v-if="showFooter" class="panel-foot">
      <div class="foot-hint">
        <Kbd>↑</Kbd>
        <Kbd>↓</Kbd>
        {{ $t("навигация") }}
      </div>
      <div class="foot-hint">
        <Kbd>↵</Kbd>
        {{ $t("выбрать") }}
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.panel {
  @apply flex flex-col bg-card border border-border rounded-[var(--radius)] overflow-hidden flex-1 min-h-0;

  &--bare {
    @apply bg-transparent border-0 rounded-none overflow-visible;

    .panel-head {
      @apply px-0;
    }

    .list-wrapper {
      //@apply px-0.5;
    }
  }
}

.panel-head {
  @apply px-1 flex flex-col gap-[10px] border-0 border-b border-solid border-border;

  &--bare {
    @apply flex justify-center py-2;
    .search-input {
      @apply border-0 bg-transparent shadow-none;

      &:focus-visible {
        @apply border-0 ring-0 shadow-none outline-none;
      }
    }
  }
}

.panel-title-row {
  @apply flex items-center justify-between px-1;
}

.panel-title {
  @apply text-[13px] font-semibold text-foreground inline-flex items-center gap-2;
}

.panel-count {
  @apply text-[11px] font-medium text-muted-foreground bg-muted rounded-full px-[7px] py-[2px] min-w-[22px] text-center;
}

.search-wrapper {
  @apply relative flex items-center;
}

.search-icon {
  @apply absolute left-3 top-1/2 -translate-y-1/2 w-[14px] h-[14px] text-muted-foreground pointer-events-none;
}

.search-input {
  @apply h-[34px];
}

.list-wrapper {
  @apply flex-1 overflow-y-auto pb-[8px] flex flex-col gap-[1px];

  &.is-locked {
    @apply pointer-events-none opacity-60;
  }
}

.loader {
  @apply flex items-center justify-center py-10;
}

.notary-list {
  @apply flex flex-col;
}

.notary-row {
  @apply flex items-center gap-[10px] px-4  py-2 cursor-pointer
  border-0 border-b border-solid border-border text-left bg-transparent w-full;

  &:hover,
  &[data-focused="true"] {
    @apply bg-muted;
  }

  &[data-focused="true"] {
    //@apply ring-1 ring-foreground/15;
  }
}

.row-info {
  @apply flex-1 min-w-0 flex flex-col gap-[1px];
}

.row-name {
  @apply text-[13px] text-foreground inline-flex items-center gap-2 min-w-0 truncate;
  font-weight: 500;
}

.row-meta {
  @apply text-[11.5px] text-muted-foreground inline-flex items-center gap-[6px]
    whitespace-nowrap overflow-hidden;
}

.row-region {
  @apply text-[10px] font-medium text-muted-foreground bg-muted rounded px-[6px] py-[2px] flex-shrink-0;
}

.row-meta-dot {
  @apply w-[2px] h-[2px] rounded-full bg-border flex-shrink-0;
}

.panel-foot {
  @apply px-[15px] py-[15px] border-t border-border flex items-center justify-between
    text-[13px] text-muted-foreground bg-muted/30;
}

.foot-hint {
  @apply inline-flex items-center gap-1;
}
</style>
